/**
 * Tripadvisor — travel reviews, plus a price-watch / deals browser helper
 *
 * Declares one web-accessible-resource entry — three HTML pages plus the whole
 * `images/` and `fonts/` folders — matched to http and https origins, with no
 * `use_dynamic_url`. So every bundled image and font is fetchable from any page at a
 * fixed URL.
 *
 * The logo images are the cleanest signal: they are vendor-named (`TA_logo.svg`,
 * `TC_Logo.png`) and, being images, load through `probeImage` with no CORS grant, so
 * they survive a restrictive page CSP.
 *
 * Manifest verified against version 1.3.0.30.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'oiekdmlabennjdpgimlcpmphdjphlcha';

export const tripadvisor: ExtensionDefinition = {
  id: ID,
  name: 'Tripadvisor: Travel Reviews & Deals on Hotels, Restaurants & Attractions',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 60_000,
  notes:
    'One static WAR entry exposing images/ and fonts/ to every http(s) origin with no ' +
    'dynamic URL. The vendor-named TC_Logo.png is conclusive and needs no CORS grant.',
  signals: [
    {
      id: 'war-tc-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/TC_Logo.png via <img> — a vendor-named logo exposed to every ' +
        'origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'images/TC_Logo.png'),
    },
    {
      id: 'war-ta-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/TA_logo.svg via <img> — a second vendor-named logo, corroborating ' +
        'the identification if either file is renamed.',
      weight: 0.5,
      run: () => probeImage(ID, 'images/TA_logo.svg'),
    },
    {
      id: 'war-newuser-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch newuser-en.html — a static onboarding page exposed to every origin, ' +
        'independent of the image paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'newuser-en.html'),
    },
  ],
};
